/**
 * Rejection-capture flow (D2, US-demo — backend: internal/twin/reject.go +
 * reject_part_date.go + reject_shortlist.go).
 *
 * THREE WAYS TO SAY WHICH CASTING, and the difference decides what the capture
 * is worth:
 *
 *   SELECT (`unit_id`) — pick the casting out of the genealogy. Always lands on
 *   the unit. Right at a terminal, where the heat list is in front of you.
 *
 *   OCR (`marks`) — photograph the cast-in ID marks (pattern / date / cavity /
 *   heat), the brain drafts the text, the user taps which-text-is-which, and the
 *   backend GUESSES the unit. Right with a casting in your hand and no terminal.
 *
 *   BY PART AND CAST DATE (`part` + `cast_date`, plan 2026-10-27 F1) — no casting
 *   at all, for a plant whose castings from many ladles mix at shakeout. Optional
 *   `cast_time` or `shift`, and `pieces`. Where an imported scrap register already
 *   books that day and part, the reject is an EXAMPLE on that lot (`example: true`):
 *   shown beside the booked figure and never counted (decision D1). Where none
 *   does, it counts, on a lot booked with its scrap blank or held as well (#1683:
 *   a lot books its day and part only with a scrap figure, and not held). An import
 *   or an undo can change that later.
 *
 * A unit-resolved capture stamps the casting, so the Pareto, the correlations and
 * the Parts photo gallery all pick it up. A capture with no unit is counted by the
 * correlations and the facts pack (corrected 2026-10-27: this note used to say an
 * unresolved one is read by nothing), but its photos are found only by its id.
 * Prefer `unit_id` wherever the caller can offer a list.
 *
 * ⚠️ There is no "later join". No worker revisits an unresolved capture to tie it
 * to a casting. Earlier copy here promised otherwise; it was never true.
 */

import { api } from "@/lib/api-client";
import type { ImageDatapoint } from "./images";
import type { OCRJob } from "./ocr";
import { qs } from "./qs";
import type { DefectHowFound } from "./twin-defect-words";

/** The four cast-in identification marks, as tagged by the user. */
export interface RejectMarks {
  pattern_no: string;
  date_code: string;
  cavity_no: string;
  heat_no: string;
}

/** The frequent-defect shortlist — config on the primary part stage's QC declaration. */
export interface RejectShortlist {
  entity_id?: string;
  entity_name?: string;
  stage_label?: string;
  defects: string[];
  /**
   * The org's primary part material_class ('raw_casting' on a green-sand plant).
   * Pass it to `genealogy.units({ heat_id, material_class })` when offering "which
   * casting?" — a heat's tracked_units include its moulds and test specimens too,
   * and it is the same class the OCR resolver filters on, so both ways in consider
   * the same castings. Do NOT hardcode 'raw_casting': the emitter uses the pour
   * entity's declared output class.
   */
  part_class?: string;
}

/**
 * A capture is either a fail (a defect was found) or a pass — a FALSE REJECT: the
 * casting was flagged, inspected, and is fine. A pass clears the casting's defect
 * and so requires `unit_id`; there is nothing to clear on a lot or a heat.
 */
export type RejectDisposition = "fail" | "pass";

/** How a capture resolved. "selected" = named outright, no guessing; "part_date" = by part and cast date. */
export type RejectResolution = "selected" | "unit" | "lot" | "heat" | "unresolved" | "part_date";

/** The most castings one reject may be (`pieces`). */
export const REJECT_PIECES_MAX = 100000;

export interface RejectSubmitInput {
  id_image_id?: string;
  /** A reject photo (`uploadPhoto`). Never a defect word's reference photo: 400 reference_photo_not_allowed. */
  defect_image_id?: string;
  /** Name the casting outright and skip resolution. Preferred — see the note above. */
  unit_id?: string;
  /** The cast-in marks (OCR). Leave out, or empty, for a reject by part and cast date. */
  marks?: RejectMarks;
  /** Defaults to "fail" server-side. */
  disposition?: RejectDisposition;
  /** Required for a fail; ignored for a pass. */
  defect_type: string;
  defect_severity?: string;
  photo_angle?: string;
  affected_area?: string;
  /**
   * Where the defect was marked on the part's 3D model (Products & Tooling).
   * `model_id` is the image_blobs id of the STL; `model_marks` is the array of
   * mesh-surface points ({x,y,z,faceIndex,label}). Opaque passthrough — the
   * backend stores them verbatim in the capture record's measurements jsonb.
   */
  model_id?: string;
  model_marks?: unknown[];
  /** 2D snapshot of the annotated 3D view — joins the casting's photo gallery. */
  model_image_id?: string;
  note?: string;

  /**
   * By part and cast date: a product of the org, by its id or its part number. With `cast_date`, and without
   * `unit_id` or `marks`. 404 part_not_found when no product has it; 400 part_has_no_number for a product with none.
   */
  part?: string;
  /** By part and cast date: the plant day it was cast on, YYYY-MM-DD, from 2000-01-01 to today on the plant clock. */
  cast_date?: string;
  /** HH:MM on the plant clock, within the cast date's plant day and not later than now. Not with `shift`. */
  cast_time?: string;
  /** A label of the org's shift calendar: placed at that shift's start on the cast date. Not with `cast_time`. */
  shift?: string;
  /** How many castings the reject is: 1 to REJECT_PIECES_MAX, 1 when left out, above 1 only by part and cast date. */
  pieces?: number;
  /** The key of a sub category of the defect's word (twinDefectWords), not retired. Any way in. */
  defect_subtype?: string;
  /** How it was found. Any way in. */
  how_found?: DefectHowFound;
  /** Where it was found: an entity of the plant model by its id, else kept as the plant's own text (one line, 200). */
  found_at?: string;
}

/** What the capture resolved to. */
export interface RejectCaptureResult {
  capture_id: string;
  resolution: RejectResolution;
  unit_id?: string;
  /**
   * The casting's lot; for part_date, the imported lot of its day and part: the lot it is an example on, or a lot
   * booked with its scrap blank or held, where it counts. Absent when no imported lot is of that day and part.
   */
  lot_id?: string;
  heat_id?: string;
  defect_type: string;
  disposition: RejectDisposition;
  /**
   * An example on the imported lot that books its day and part with a scrap figure and is not held: shown beside the
   * booked figure, never counted (D1).
   * False for every other capture. An import that later books the day and part, or its undo, can change it.
   */
  example: boolean;
  /** How many castings it is (1 unless logged by part and cast date). */
  pieces: number;
  defect_subtype?: string;
  /** part_date only. */
  product_id?: string;
  part_number?: string;
  cast_date?: string;
  /** part_date only: where it was placed (its cast time, its shift's start, or noon of its day, never after the submit). */
  measured_at?: string;
}

/** The one record a reject capture's QC row carries (`measurements[0]`), as far as a page shows it. */
export interface RejectCaptureRecord {
  kind: "reject_capture";
  resolution: RejectResolution;
  disposition: string;
  id_image_id?: string;
  defect_image_id?: string;
  model_snapshot_image_id?: string;
  note?: string;
  product_id?: string;
  part_number?: string;
  cast_date?: string;
  cast_time?: string;
  shift?: string;
  pieces?: number;
  defect_subtype?: string;
  how_found?: DefectHowFound;
  /** Where it was found: an entity id, or the plant's own text in `found_at`. */
  found_at_entity?: string;
  found_at?: string;
}

/** One QC record (GET /v1/twin/qc-results): a lot's booked record, or a floor capture. */
export interface QCResultRecord {
  id: string;
  test_type: string;
  measured_at: string;
  pass: boolean;
  /** Pieces per word. */
  defects: Record<string, number>;
  /** For a reject capture, one RejectCaptureRecord; an import's lot record holds its cast and scrap. */
  measurements: unknown[];
  lot_id?: string;
  batch_id?: string;
  unit_id?: string;
  source: "generated" | "import" | "capture" | "unknown";
  /** A floor reject shown beside its lot's booked figure and counted by no reader (D1). */
  example: boolean;
  /** A reject logged by part and cast date: its plant day and part. */
  cast_date?: string;
  part_number?: string;
}

export interface QCResultList {
  items: QCResultRecord[];
  total: number;
  limit: number;
  offset: number;
}

export interface RejectPhotoInput {
  file: Blob;
  compressed?: boolean;
  width?: number;
  height?: number;
  filename?: string;
}

export const rejectCapture = {
  /** Store one reject-flow photo (ID marks or defect) → image datapoint. */
  uploadPhoto: (input: RejectPhotoInput) => {
    const fd = new FormData();
    fd.append("file", input.file, input.filename ?? "photo");
    fd.append("compressed", input.compressed ? "true" : "false");
    if (input.width) fd.append("width", String(input.width));
    if (input.height) fd.append("height", String(input.height));
    return api.postForm<ImageDatapoint>("/v1/twin/reject-captures/photos", fd);
  },

  /** Enqueue a register-less Claude-vision OCR job for an uploaded image. */
  enqueueOcr: (imageId: string) =>
    api.post<OCRJob>(`/v1/twin/images/${imageId}/ocr`, {}),

  /** The foundry's frequent-defect shortlist (capture-readable). */
  shortlist: () => api.get<RejectShortlist>("/v1/twin/reject-captures/shortlist"),

  /** Replace the shortlist (model.write — authoring). */
  saveShortlist: (defects: string[]) =>
    api.put<RejectShortlist>("/v1/twin/reject-captures/shortlist", { defects }),

  /**
   * Submit the capture — stamps the casting and logs the record. Pass `unit_id`
   * when you know which casting; fall back to `marks` only when you don't; and
   * `part` + `cast_date` where the plant knows only those.
   */
  submit: (input: RejectSubmitInput) =>
    api.post<RejectCaptureResult>("/v1/twin/reject-captures", input),

  /**
   * A lot's reject records, newest first: its booked record and the floor rejects attached to it as examples
   * (`example: true`), to show beside the booked figure.
   */
  lotRecords: (lotId: string, page: { limit?: number; offset?: number } = {}) =>
    api.get<QCResultList>(`/v1/twin/qc-results${qs({ lot_id: lotId, test_type: "reject_capture", ...page })}`),
};
